import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { AddPositionProps, schema, signupPositionSchema } from "./components.utils";
import { useForm, useWatch } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { FormMaterialInput } from "../../../../components/Inputs/MaterialInput";
import { FormMaterialSelector } from "../../../../components/Selector";
import { MenuItem, CircularProgress } from "@material-ui/core";
import { FormCheckboxInput } from "../../../../components/Inputs/CheckboxInput";
import { FormMaterialCalendarPicker } from "../../../../components/Inputs/MaterialCalendarPicker";
import CloseIcon from '@material-ui/icons/Close';
import dayjs from "dayjs";
import styles from "../../../hygienist/ProfilePage/components/components.module.scss";

// @ts-ignore
const TempStars: any = window.TempStars;

export const AddPosition = ({ country, onAdd, isOpen, onClose, position, normalizeSignupFields = false }: AddPositionProps) => {
    const [loading, setLoading] = useState(false);
    const {
        control,
        handleSubmit,
        setValue,
        watch
    } = useForm({
        defaultValues: {
            jobTitle: position?.jobTitle || '',
            companyName: position?.companyName || '',
            city: position?.city || '',
            employmentType: position?.employmentType || '',
            current: !!position?.current || false,
            fromDate: position?.fromDate || dayjs().set("day", 1),
            toDate: position?.toDate || dayjs().set("day", 1),
        },
        resolver: joiResolver(normalizeSignupFields ? signupPositionSchema : schema) as any,
    });
    const [inputValue, setInputValue] = useState(position?.city || '');
    const autocompleteRef = useRef<any>(null);

    useEffect(() => {
        let autocompleteInstance: any;

        const initAutocomplete = () => {
            const input = document.getElementById('autocomplete') as HTMLInputElement;
            if (input) {
                autocompleteInstance = TempStars.Autocomplete.init(
                    {
                        country: country,
                        types: ['(cities)']
                    },
                    (place: { city?: string, valueEqual?: boolean }) => {
                        if (place && place.city) {
                            setInputValue(place.city);
                            setValue('city', place.city, { shouldValidate: true });
                        }
                    }
                );

                input.addEventListener('input', (event) => {
                    const newValue = (event.target as HTMLInputElement).value;
                    setInputValue(newValue);
                    setValue('city', newValue, { shouldValidate: false });
                });
            }

            return () => {
                if (autocompleteInstance) {
                    TempStars.Autocomplete.removeListener();
                }
                if (input) {
                    input.removeEventListener('input', () => { });
                }
            };
        };

        return initAutocomplete();
    }, [country, setValue]);

    const current = useWatch({ control, name: "current" })
    if (current) {
        setValue('toDate', dayjs())
    }

    const onSubmit = async (data: any) => {
        setLoading(true);
        try {
            await onAdd(data);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.wizardModalOverlay} onClick={onClose}>
            <div className={styles.wizardModalContainer} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className={styles.wizardModalHeader}>
                    <div className={styles.wizardHeaderContent}>
                        <h1 className={styles.wizardModalTitle}>
                            {position ? 'Edit Position' : 'Add Position'}
                        </h1>
                        <p className={styles.wizardModalSubtitle}>
                            {position ? 'Update your work experience details' : 'Add a new work experience entry'}
                        </p>
                    </div>
                    <button className={styles.wizardCloseButton} onClick={onClose}>
                        <CloseIcon />
                    </button>
                </div>

                {/* Body */}
                <form className={styles.wizardModalBody} onSubmit={handleSubmit(onSubmit)}>
                    <div className={styles.wizardInputContainer}>
                        <FormMaterialInput
                            control={control}
                            label="Job Title"
                            name={"jobTitle"}
                        />
                    </div>

                    <div className={styles.wizardInputContainer}>
                        <FormMaterialInput
                            control={control}
                            label="Company Name"
                            name={"companyName"}
                        />
                    </div>

                    <div className={styles.wizardInputContainer}>
                        <FormMaterialInput
                            control={control}
                            name="city"
                            label="Start typing a city name..."
                            id="autocomplete"
                            className={'no-fastclick'}
                            inputProps={{
                                value: inputValue,
                                onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                    const newValue = e.target.value;
                                    setInputValue(newValue);
                                },
                            }}
                        />
                    </div>

                    <div className={styles.wizardInputContainer}>
                        <FormMaterialSelector
                            control={control}
                            label="Employment Type"
                            name={"employmentType"}
                        >
                            <MenuItem key={'fulltime'} value={'fulltime'}>
                                Full-time
                            </MenuItem>
                            <MenuItem key={'parttime'} value={'parttime'}>
                                Part-time
                            </MenuItem>
                            <MenuItem key={'contract'} value={'contract'}>
                                Contract
                            </MenuItem>
                        </FormMaterialSelector>
                    </div>

                    <div className={styles.wizardFormSection}>
                        <div
                            className={clsx(styles.wizardRadioOption)}
                            onClick={() => setValue('current', !current)}
                            style={{ cursor: 'pointer' }}
                        >
                            <input
                                type="checkbox"
                                checked={current}
                                onChange={(e) => setValue('current', e.target.checked)}
                            />
                            <div className={styles.radioContent}>
                                <div className={styles.radioLabel}>I am currently working in this role</div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.wizardFormSection}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div className={styles.wizardInputContainer} style={{ marginBottom: 0 }}>
                                <FormMaterialCalendarPicker
                                    control={control}
                                    name={'fromDate'}
                                    label={'From'}
                                    format={'MMM YYYY'}
                                    views={['month', 'year']}
                                />
                            </div>
                            <div className={styles.wizardInputContainer} style={{ marginBottom: 0 }}>
                                <FormMaterialCalendarPicker
                                    control={control}
                                    name={'toDate'}
                                    label={'To'}
                                    format={'MMM YYYY'}
                                    views={['month', 'year']}
                                    disabled={current}
                                />
                            </div>
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className={styles.wizardModalFooter}>
                    <button
                        type="button"
                        className={styles.wizardCancelButton}
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className={styles.wizardSaveButton}
                        onClick={handleSubmit(onSubmit)}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </button>
                </div>

                {/* Loader Overlay */}
                {loading && (
                    <div className={styles.wizardLoaderOverlay}>
                        <CircularProgress />
                    </div>
                )}
            </div>
        </div>
    );
}
